import { ServiceProvider } from "../dependencies_management/ServiceProvider";
import { ExecutionContext } from "../types/ExecutionContext";
import { InternalEventsSender } from "../utilities/internal_events/InternalEventsSender";
import { AnalyticsPageNameProvider } from "../utilities/page_name/AnalyticsPageNameProvider";
import { WindowLocationWrapper } from "../utilities/window_wrapping/WindowLocationWrapper";
import { ContractSectionsTracker } from "./ContractSectionsTracker";
import { SECTION_TRACKING_BASE_URL } from "../utilities/internal_events/InternalEventsBaseUrls";
import { InternalPageviewAutomaticTrackingChecker } from "../utilities/internal_events/InternalPageviewAutomaticTrackingChecker";

/**
 * Used for tracking contract groups/sections.
 */
export class DefaultContractSectionsTracker implements ContractSectionsTracker {
	private readonly internalEventsSender: InternalEventsSender;
	private readonly analyticsPageNameProvider: AnalyticsPageNameProvider;
	private readonly windowLocation: WindowLocationWrapper;
	private readonly internalPageviewAutomaticTrackingChecker: InternalPageviewAutomaticTrackingChecker;

	private currentSection: string | undefined = undefined;
	private currentGroup: string | undefined = undefined;

	constructor(
		serviceProvider: ServiceProvider,
		private readonly isUserLicensed: boolean
	) {
		this.internalEventsSender = serviceProvider.getInternalEventsSender(
			SECTION_TRACKING_BASE_URL
		);
		this.analyticsPageNameProvider =
			serviceProvider.getAnalyticsPageNameProvider();
		this.windowLocation = serviceProvider.getWindowLocationWrapper();
		this.internalPageviewAutomaticTrackingChecker =
			serviceProvider.getInternalPageviewAutomaticTrackingChecker();
	}

	executionContext!: ExecutionContext;

	track(newSection: string, newGroup: string): void {
		if (
			this.haveSectionAndGroupAlreadyBeenTracked(newSection, newGroup) ||
			(this.isPreviewPage() &&
				this.areNotPreviewSectionAndGroup(newSection, newGroup)) ||
			this.hasPageviewAlreadyBeenSent()
		) {
			return;
		}

		const previousSection = this.currentSection;
		const previousGroup = this.currentGroup;
		this.currentSection = newSection;
		this.currentGroup = newGroup;

		const eventData: Record<string, string> = {
			ps: previousSection ?? "",
			pg: previousGroup ?? "",
			cs: this.currentSection ?? "",
			cg: this.currentGroup ?? "",
			lc: this.isUserLicensed.toString(),
			pc: this.executionContext.productCode ?? "",
		};

		this.internalEventsSender.sendEvent(eventData);
	}

	/* If we're on the preview page don't report anything but 'preview' views
	There is one bug where landing page views get reported
	Another bug where the checklist gets reported */
	private areNotPreviewSectionAndGroup(section: string, group: string) {
		return (
			section.toLowerCase() !== "preview" && group.toLowerCase() !== "preview"
		);
	}

	private isPreviewPage(): boolean {
		return this.windowLocation.pathname.toLowerCase().includes("preview");
	}

	/**
	 * If we're navigating to the exact same section and group don't bother tracking anything.
	 * There exists a bug where one call will be made properly then a second call with matching previous and current.
	 * Also, if a user just clicks the section they're currently on it will call a track but nothing actually happened.
	 * So, we don't need to track that.
	 *
	 * @param section The section to use for the check.
	 * @param group The group to use for the check.
	 * @returns True if the section and group have already been tracked previously; false otherwise.
	 */
	private haveSectionAndGroupAlreadyBeenTracked(
		section: string,
		group: string
	): boolean {
		return this.currentSection === section && this.currentGroup === group;
	}

	/**
	 * We don't want to submit a pageview on these pages because it happens automatically on the initialization of the tracking library.
	 *
	 * @returns True if it is expected that pageview has already been sent; false otherwise.
	 */
	private hasPageviewAlreadyBeenSent(): boolean {
		const analyticsPageName = this.analyticsPageNameProvider.providePageName(
			this.executionContext
		);
		return this.internalPageviewAutomaticTrackingChecker.shouldTrackPage(
			analyticsPageName
		);
	}
}
